'use client';

import React, { useState, useCallback } from 'react';
import { CustomImage as Image, useCartStore, useShallow } from '..';
import { SupplementProduct } from 'content/products/supplements';
import { ChevronRight } from 'assets/icons/ChevronRight';
import { CrossBVModal } from 'assets/icons/CrossBVModal';
import { ShippingBox } from 'assets/icons';
import { DotSpinner } from 'components/shared';
import {
  CurrencySymbol,
  useFormatPrice,
} from 'hooks/pricing/useFormattedPrice';
import { Currency } from 'utils/Currency';
import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import { ProductType } from 'components/shared/Cart/types';
import { useTranslations } from 'next-intl';
import styles from './SupplementLearnMoreModal.module.css';

interface SupplementLearnMoreModalProps {
  product: SupplementProduct;
  onClose: () => void;
}

const cx = (...c: (string | undefined | false)[]) =>
  c.filter(Boolean).join(' ');

const ModalContainer = (props: React.HTMLAttributes<HTMLDivElement>) => (
  <div {...props} className={cx(styles.modalContainer, props.className)} />
);

const CloseButton = (props: React.ButtonHTMLAttributes<HTMLButtonElement>) => (
  <button {...props} className={cx(styles.closeButton, props.className)} />
);

const ModalContent = (props: React.HTMLAttributes<HTMLDivElement>) => (
  <div {...props} className={cx(styles.modalContent, props.className)} />
);

const HeaderSection = (props: React.HTMLAttributes<HTMLDivElement>) => (
  <div {...props} className={cx(styles.headerSection, props.className)} />
);

const ThumbnailContainer = (props: React.HTMLAttributes<HTMLDivElement>) => (
  <div {...props} className={cx(styles.thumbnailContainer, props.className)} />
);

const HeaderInfo = (props: React.HTMLAttributes<HTMLDivElement>) => (
  <div {...props} className={cx(styles.headerInfo, props.className)} />
);

const ProductName = (props: React.HTMLAttributes<HTMLHeadingElement>) => (
  <h2 {...props} className={cx(styles.productName, props.className)} />
);

const HeaderPrice = (props: React.HTMLAttributes<HTMLSpanElement>) => (
  <span {...props} className={cx(styles.headerPrice, props.className)} />
);

const ShippingInfo = (props: React.HTMLAttributes<HTMLParagraphElement>) => (
  <p {...props} className={cx(styles.shippingInfo, props.className)} />
);

const Divider = (props: React.HTMLAttributes<HTMLDivElement>) => (
  <div {...props} className={cx(styles.divider, props.className)} />
);

const StickyFooter = (props: React.HTMLAttributes<HTMLDivElement>) => (
  <div {...props} className={cx(styles.stickyFooter, props.className)} />
);

const FooterButton = (props: React.ButtonHTMLAttributes<HTMLButtonElement>) => (
  <button {...props} className={cx(styles.footerButton, props.className)} />
);

const DescriptionSection = (props: React.HTMLAttributes<HTMLDivElement>) => (
  <div {...props} className={cx(styles.descriptionSection, props.className)} />
);

const SectionHeader = (
  props: React.ButtonHTMLAttributes<HTMLButtonElement>,
) => (
  <button {...props} className={cx(styles.sectionHeader, props.className)} />
);

const SectionTitle = (props: React.HTMLAttributes<HTMLHeadingElement>) => (
  <h3 {...props} className={cx(styles.sectionTitle, props.className)} />
);

const ChevronIcon = ({
  $isOpen,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & { $isOpen: boolean }) => (
  <div
    {...props}
    data-open={$isOpen ? 'true' : 'false'}
    className={cx(styles.chevronIcon, props.className)}
  />
);

const CollapsibleContent = ({
  $isOpen,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & { $isOpen: boolean }) => (
  <div
    {...props}
    data-open={$isOpen ? 'true' : 'false'}
    className={cx(styles.collapsibleContent, props.className)}
  />
);

const CollapsibleInner = ({
  $isOpen,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & { $isOpen: boolean }) => (
  <div
    {...props}
    data-open={$isOpen ? 'true' : 'false'}
    className={cx(styles.collapsibleInner, props.className)}
  />
);

const DescriptionText = (props: React.HTMLAttributes<HTMLParagraphElement>) => (
  <p {...props} className={cx(styles.descriptionText, props.className)} />
);

const DetailsList = (props: React.HTMLAttributes<HTMLUListElement>) => (
  <ul {...props} className={cx(styles.detailsList, props.className)} />
);

const DetailItem = (props: React.LiHTMLAttributes<HTMLLIElement>) => (
  <li {...props} className={cx(styles.detailItem, props.className)} />
);

const GroupedDetails = (props: React.HTMLAttributes<HTMLDivElement>) => (
  <div {...props} className={cx(styles.groupedDetails, props.className)} />
);

const GroupTitle = (props: React.HTMLAttributes<HTMLHeadingElement>) => (
  <h4 {...props} className={cx(styles.groupTitle, props.className)} />
);

type SectionKey =
  | 'about'
  | 'ingredients'
  | 'benefits'
  | 'dailyServing'
  | 'whenToTake'
  | 'howToUse'
  | 'disclaimer';

const SupplementLearnMoreModal: React.FC<SupplementLearnMoreModalProps> = ({
  product,
  onClose,
}) => {
  const CartClient = useCartStore(
    useShallow((s) => ({
      store: s.store,
      currency: s.currency,
      addToCart: s.addToCart,
    })),
  );
  const [isLoading, setIsLoading] = useState(false);
  const tCart = useTranslations('cart');
  const t = useTranslations('ring');
  const [openSections, setOpenSections] = useState<Record<SectionKey, boolean>>(
    {
      about: true,
      ingredients: false,
      benefits: true,
      dailyServing: false,
      whenToTake: false,
      howToUse: false,
      disclaimer: false,
    },
  );

  const variantId = product.data?.[CartClient.store as ShopifyStore]
    ?.shopifyVariantId as string | undefined;

  const price =
    (product.data?.[CartClient.store as ShopifyStore]?.price as number) ??
    product.price;

  const currencySymbol =
    CurrencySymbol[(CartClient.currency ?? Currency.usd) as Currency];

  const isSoldOut = product.tag === 'sold out';

  const handleAddToCart = async () => {
    if (!variantId || isSoldOut) return;
    try {
      setIsLoading(true);
      await CartClient.addToCart([
        {
          shopifyVariantId: variantId,
          productType: ProductType.SUPPLEMENT,
        },
      ]);
      onClose();
    } finally {
      setIsLoading(false);
    }
  };

  const toggleSection = useCallback((section: SectionKey) => {
    setOpenSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  }, []);
  const formatPriceWithComma = useFormatPrice();

  // No disclaimer copy (string or list) = no empty Disclaimer accordion.
  const hasDisclaimer = Boolean(
    product.learnMore.disclaimers?.length || product.learnMore.disclaimer,
  );
  // Dosing sits above Benefits. Thorne products set neither field and keep
  // `howToUse`, so their section order is unchanged.
  const sections: { key: SectionKey; title: string }[] = [
    { key: 'about', title: t('ring.supplementModal.about') },
    { key: 'ingredients', title: t('ring.supplementModal.ingredients') },
    ...(product.learnMore.dailyServing
      ? [
          {
            key: 'dailyServing' as SectionKey,
            title: t('ring.supplementModal.dailyServing'),
          },
        ]
      : []),
    ...(product.learnMore.whenToTake
      ? [
          {
            key: 'whenToTake' as SectionKey,
            title: t('ring.supplementModal.whenToTake'),
          },
        ]
      : []),
    { key: 'benefits', title: t('ring.supplementModal.benefits') },
    ...(product.learnMore.howToUse
      ? [
          {
            key: 'howToUse' as SectionKey,
            title: t('ring.supplementModal.howToUse'),
          },
        ]
      : []),
    ...(hasDisclaimer
      ? [
          {
            key: 'disclaimer' as SectionKey,
            title: t('ring.supplementModal.disclaimer'),
          },
        ]
      : []),
  ];

  const renderSectionContent = (key: SectionKey) => {
    const { learnMore } = product;
    switch (key) {
      case 'about':
        return <DescriptionText>{learnMore.about}</DescriptionText>;
      case 'ingredients':
        // Luminary's Essential/Other split; ungrouped products keep the flat list.
        return learnMore.ingredientGroups?.length ? (
          <GroupedDetails>
            {learnMore.ingredientGroups.map((group) => (
              <div key={group.title}>
                <GroupTitle>{group.title}</GroupTitle>
                <DetailsList>
                  {group.items.map((item, idx) => (
                    <DetailItem key={idx}>{item}</DetailItem>
                  ))}
                </DetailsList>
              </div>
            ))}
          </GroupedDetails>
        ) : (
          <DetailsList>
            {learnMore.ingredients.map((item, idx) => (
              <DetailItem key={idx}>{item}</DetailItem>
            ))}
          </DetailsList>
        );
      case 'benefits':
        return (
          <DetailsList>
            {learnMore.benefits.map((item, idx) => (
              <DetailItem key={idx}>{item}</DetailItem>
            ))}
          </DetailsList>
        );
      case 'dailyServing':
        return <DescriptionText>{learnMore.dailyServing}</DescriptionText>;
      case 'whenToTake':
        return <DescriptionText>{learnMore.whenToTake}</DescriptionText>;
      case 'howToUse':
        return <DescriptionText>{learnMore.howToUse}</DescriptionText>;
      case 'disclaimer':
        return learnMore.disclaimers?.length ? (
          <DetailsList>
            {learnMore.disclaimers.map((item, idx) => (
              <DetailItem key={idx}>{item}</DetailItem>
            ))}
          </DetailsList>
        ) : (
          <DescriptionText>{learnMore.disclaimer}</DescriptionText>
        );
    }
  };

  return (
    <ModalContainer>
      <CloseButton onClick={onClose}>
        <CrossBVModal />
      </CloseButton>

      <HeaderSection>
        <ThumbnailContainer>
          <Image
            src={product.imgSrc}
            alt={product.title}
            width={64}
            height={64}
            style={{ objectFit: 'cover' }}
          />
        </ThumbnailContainer>
        <HeaderInfo>
          <ProductName>{product.title}</ProductName>
          <HeaderPrice>
            {currencySymbol}
            {formatPriceWithComma(price)}
          </HeaderPrice>
        </HeaderInfo>
      </HeaderSection>

      <ShippingInfo>
        <ShippingBox />{' '}
        {product.shippingNote ?? t('ring.shop.supplementsShipping')}
      </ShippingInfo>

      <ModalContent>
        {sections.map(({ key, title }, idx) => (
          <React.Fragment key={key}>
            <DescriptionSection>
              <SectionHeader onClick={() => toggleSection(key)}>
                <SectionTitle>{title}</SectionTitle>
                <ChevronIcon $isOpen={openSections[key]}>
                  <ChevronRight fill="#000000" />
                </ChevronIcon>
              </SectionHeader>
              <CollapsibleContent $isOpen={openSections[key]}>
                <CollapsibleInner $isOpen={openSections[key]}>
                  {renderSectionContent(key)}
                </CollapsibleInner>
              </CollapsibleContent>
            </DescriptionSection>
            {idx < sections.length - 1 && <Divider />}
          </React.Fragment>
        ))}
      </ModalContent>

      <StickyFooter>
        <FooterButton
          onClick={handleAddToCart}
          disabled={!variantId || isLoading || isSoldOut}
          aria-busy={isLoading}
        >
          {isLoading ? (
            <DotSpinner color="#FFFFFF" />
          ) : isSoldOut ? (
            tCart('sharedCart.upsell.soldOut')
          ) : variantId ? (
            tCart('sharedCart.upsell.addToCart')
          ) : (
            t('ring.shop.comingSoon')
          )}
        </FooterButton>
      </StickyFooter>
    </ModalContainer>
  );
};

export default SupplementLearnMoreModal;
